import { Box, ButtonBase, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { getImportIdLabel } from '../utils/patientFormConfig'
import { usePatientFormI18n } from '../utils/patientFormI18n'
import { PatientAlternativeEmails, PatientAlternativeMobiles } from './PatientAlternateContacts'
import PatientAgeField from './PatientAgeField'
import PatientCityField from './PatientCityField'
import {
  PATIENT_FORM_TOKENS as T,
  PatientCard,
  PatientChip,
  PatientFieldHint,
  PatientFieldLabel,
  PatientFormCell,
  PatientFormField,
  PatientFormGrid,
  PatientInfoBanner,
  PatientNativeSelect,
  PatientSegmentedControl,
  PatientTextInput,
} from './PatientFormPrimitives'
import PatientPhoneField from './PatientPhoneField'
import PatientSearchDropdown from './PatientSearchDropdown'

const HALF = { xs: 12, md: 6 }

/**
 * The always-visible part of the form: how to reach the patient and who they are.
 * Order follows how a front desk actually works — number first (it doubles as the
 * existing-patient search), then name, gender and age.
 */
export const PatientEssentialsSection = ({
  patientDetails,
  editMode = true,
  formHook,
  validationHook,
  searchHook,
  visibilityHook,
  makeAnonymousPatient,
  isForcingAnonymousPatient = false,
  importIdFieldLabel = 'Import Id',
  clinicId,
  clinicBrandId,
  patientSearchApiSignature,
  disablePatientSearch = false,
  autoFocusMobileField = false,
  canToggleNoContact = false,
  countryIso,
}) => {
  const { copy, genderOptions, relationshipOptions } = usePatientFormI18n()
  const disabled = !editMode
  const isExistingPatient = Boolean(patientDetails?.id)
  // The "no mobile or email" switch is a create-time choice; existing records keep it read-only.
  const showNoContactToggle = canToggleNoContact && editMode && !isForcingAnonymousPatient && !isExistingPatient
  const hasNoContactOnFile =
    isExistingPatient && (Boolean(patientDetails?.internalNoContactEmail) || Boolean(patientDetails?.anonymous))

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !autoFocusMobileField ||
      makeAnonymousPatient ||
      !searchHook?.inputRef?.current
    ) {
      return undefined
    }

    const focusTimer = window.setTimeout(() => {
      searchHook.inputRef.current?.focus?.()
    }, 150)

    return () => {
      window.clearTimeout(focusTimer)
    }
  }, [autoFocusMobileField, makeAnonymousPatient, searchHook?.inputRef])

  const mobileValidation = validationHook.getFieldValidation('mobile')
  const emailValidation = validationHook.getFieldValidation('email')
  const nameValidation = validationHook.getFieldValidation('name')
  const genderValidation = validationHook.getFieldValidation('gender')
  const ageValidation = validationHook.getFieldValidation('age')
  const showAlternativeMobiles = visibilityHook.isFieldVisible('alternativeMobiles')
  // Required-but-empty tint (only while editing): mobile until contact is valid, name, gender, age.
  const hasContact = Boolean(patientDetails?.mobile) || Boolean(patientDetails?.email)
  const mobileRequiredEmpty = editMode && !makeAnonymousPatient && !hasContact
  const nameRequiredEmpty = editMode && !nameValidation.isValid
  const genderRequiredEmpty = editMode && !genderValidation.isValid
  const ageRequiredEmpty = editMode && !ageValidation.isValid

  const emailField = visibilityHook.shouldShowEmailInBasicInfo && (
    <PatientFormField
      label={copy.emailLabel}
      span={HALF}
      htmlFor="patient-email"
      error={emailValidation.showError ? emailValidation.errorMessage : null}
      footer={
        visibilityHook.shouldShowAlternativeEmailsInBasicInfo ? (
          <PatientAlternativeEmails
            alternativeEmails={patientDetails?.altEmails}
            onAdd={formHook.addAlternativeEmail}
            onUpdate={formHook.updateAlternativeEmail}
            onRemove={formHook.removeAlternativeEmail}
            editMode={editMode}
            validationHook={validationHook}
          />
        ) : null
      }
    >
      <PatientTextInput
        id="patient-email"
        type="email"
        inputMode="email"
        autoComplete="off"
        value={patientDetails?.email || ''}
        onChange={formHook.handleEmailChange}
        onBlur={() => validationHook.markTouched('email')}
        disabled={disabled}
        invalid={emailValidation.showError}
        placeholder={copy.emailPlaceholder}
      />
    </PatientFormField>
  )

  return (
    <PatientFormGrid>
      {isForcingAnonymousPatient && (
        <PatientFormCell span={12}>
          <PatientInfoBanner>{copy.contactDetailsRestrictedMessage}</PatientInfoBanner>
        </PatientFormCell>
      )}

      {visibilityHook.isFieldVisible('mobile') && (
        <PatientPhoneField
          label={copy.mobileLabel}
          required
          span={HALF}
          value={patientDetails?.mobile}
          onChange={(value) => {
            searchHook.clearLoadedOptions()
            formHook.handleMobileChange(value)
          }}
          onFocus={searchHook.handleMobileFocus}
          onBlur={() => {
            searchHook.handleMobileBlur()
            validationHook.markTouched('mobile')
          }}
          onKeyDown={searchHook.handlePhoneInputKeyDown}
          disabled={disabled}
          placeholder={copy.mobilePlaceholder}
          error={mobileValidation.showError ? mobileValidation.errorMessage : null}
          touched={Boolean(validationHook.touchedFields?.mobile)}
          requiredEmpty={mobileRequiredEmpty}
          inputProps={{ ref: searchHook.inputRef, autoFocus: autoFocusMobileField }}
          labelAddon={
            showNoContactToggle ? (
              <ButtonBase
                onClick={() => formHook.handleAnonymousPatientChange(true)}
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: T.accent,
                  padding: '2px 6px',
                  marginRight: '-6px',
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  '&:hover': { background: T.accentSoft },
                  '&.Mui-focusVisible': { boxShadow: `0 0 0 3px ${T.accentRing}` },
                }}
              >
                {copy.noContactToggleLabel}
              </ButtonBase>
            ) : null
          }
          overlay={
            !disablePatientSearch ? (
              <PatientSearchDropdown
                searchHook={searchHook}
                clinicId={clinicId}
                clinicBrandId={clinicBrandId}
                patientSearchApiSignature={patientSearchApiSignature}
              />
            ) : null
          }
          footer={
            showAlternativeMobiles ? (
              <PatientAlternativeMobiles
                alternativeMobiles={patientDetails?.altMobiles}
                onAdd={formHook.addAlternativeMobile}
                onUpdate={formHook.updateAlternativeMobile}
                onRemove={formHook.removeAlternativeMobile}
                editMode={editMode}
                validationHook={validationHook}
              />
            ) : null
          }
        />
      )}

      {emailField}

      {makeAnonymousPatient && !isForcingAnonymousPatient && (
        <PatientFormCell span={12}>
          <PatientCard tone="accent" data-testid="patient-no-contact-mode">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
                <Typography component="div" sx={{ fontSize: '13.5px', fontWeight: 700, color: '#312e81' }}>
                  {copy.noContactModeTitle}
                </Typography>
                <PatientFieldHint sx={{ marginTop: '4px', color: '#3730a3' }}>
                  {copy.noContactModeBody}
                </PatientFieldHint>
              </Box>
              {showNoContactToggle && (
                <ButtonBase
                  onClick={() => formHook.handleAnonymousPatientChange(false)}
                  sx={{
                    fontSize: '12.5px',
                    fontWeight: 700,
                    color: T.accent,
                    padding: '6px 10px',
                    borderRadius: '8px',
                    border: `1px solid rgba(103, 121, 227, 0.4)`,
                    background: T.surface,
                    whiteSpace: 'nowrap',
                    minHeight: { xs: '40px', sm: '32px' },
                    '&:hover': { background: T.accentSoft },
                    '&.Mui-focusVisible': { boxShadow: `0 0 0 3px ${T.accentRing}` },
                  }}
                >
                  {copy.noContactModeUndo}
                </ButtonBase>
              )}
            </Box>
          </PatientCard>
        </PatientFormCell>
      )}

      {hasNoContactOnFile && !makeAnonymousPatient && (
        <PatientFormCell span={12} sx={{ marginTop: '-6px' }}>
          <PatientChip tone="muted">{copy.noContactOnFileLabel}</PatientChip>
        </PatientFormCell>
      )}

      {visibilityHook.shouldShowRelationDropdown && (
        <PatientFormCell span={12}>
          <PatientCard tone="accent">
            <PatientFieldLabel htmlFor="patient-relation">
              {copy.relatedPatientTitlePrefix}{' '}
              <b>{patientDetails?.relatedToExistingPatient?.name}</b>
              {copy.relatedPatientTitleSuffix}
            </PatientFieldLabel>
            <Box sx={{ maxWidth: { md: '320px' } }}>
              <PatientNativeSelect
                id="patient-relation"
                value={patientDetails?.dependentRelation || ''}
                onChange={formHook.handleRelationChange}
                options={relationshipOptions.map((option) => ({
                  ...option,
                  label: copy.relationOfLabel(option.label, patientDetails?.relatedToExistingPatient?.name || ''),
                }))}
                disabled={disabled}
              />
            </Box>
            <PatientFieldHint sx={{ marginTop: '6px' }}>{copy.relatedPatientHelper}</PatientFieldHint>
          </PatientCard>
        </PatientFormCell>
      )}

      {visibilityHook.isFieldVisible('name') && (
        <PatientFormField
          label={copy.nameLabel}
          required
          span={HALF}
          htmlFor="patient-name"
          error={nameValidation.showError ? nameValidation.errorMessage : null}
          requiredEmpty={nameRequiredEmpty}
        >
          <PatientTextInput
            id="patient-name"
            autoComplete="off"
            value={patientDetails?.name || ''}
            onChange={formHook.handleNameChange}
            onBlur={() => validationHook.markTouched('name')}
            disabled={disabled}
            invalid={nameValidation.showError}
          />
        </PatientFormField>
      )}

      {visibilityHook.isFieldVisible('gender') && (
        <PatientFormField
          label={copy.genderLabel}
          required
          span={HALF}
          error={genderValidation.showError ? copy.validationChooseOne : null}
          requiredEmpty={genderRequiredEmpty}
        >
          <PatientSegmentedControl
            requiredEmpty={genderRequiredEmpty}
            ariaLabel={copy.genderLabel}
            options={genderOptions}
            value={patientDetails?.gender}
            onChange={(id) => {
              formHook.handleGenderChange(id)
              validationHook.markTouched('gender')
            }}
            disabled={disabled}
            invalid={genderValidation.showError}
          />
        </PatientFormField>
      )}

      {visibilityHook.isFieldVisible('age') && (
        <PatientAgeField
          patientDetails={patientDetails}
          editMode={editMode}
          formHook={formHook}
          validationHook={validationHook}
          span={HALF}
          requiredEmpty={ageRequiredEmpty}
        />
      )}

      {visibilityHook.isFieldVisible('city') && (
        <PatientCityField
          patientDetails={patientDetails}
          editMode={editMode}
          formHook={formHook}
          countryIso={countryIso}
          span={HALF}
        />
      )}

      {visibilityHook.isFieldVisible('uhid') && (
        <PatientFormField label={copy.uhidLabel} span={HALF} htmlFor="patient-uhid">
          <PatientTextInput
            id="patient-uhid"
            autoComplete="off"
            value={patientDetails?.uhId || ''}
            onChange={formHook.handleUhidChange}
            disabled={disabled}
          />
        </PatientFormField>
      )}

      {visibilityHook.isFieldVisible('importId') && (
        <PatientFormField
          label={getImportIdLabel(importIdFieldLabel)}
          span={HALF}
          htmlFor="patient-import-id"
        >
          <PatientTextInput
            id="patient-import-id"
            autoComplete="off"
            value={patientDetails?.importData?.importedFromId || ''}
            onChange={formHook.handleImportIdChange}
            disabled={disabled}
          />
        </PatientFormField>
      )}

      {visibilityHook.isFieldVisible('patientNumber') && patientDetails?.patientNumber && (
        <PatientFormField label={copy.patientNumberLabel} span={HALF}>
          <Box
            sx={{
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              fontSize: '14px',
              fontWeight: 600,
              color: '#334155',
            }}
          >
            {patientDetails.patientNumber}
          </Box>
        </PatientFormField>
      )}
    </PatientFormGrid>
  )
}

export default PatientEssentialsSection
